import { z } from 'zod/v4';
import { Direction } from '../../interactions/index.js';
import { PerformedBySchema } from '../../performed-by/index.js';
import { ToolCategory, ToolName, ToolPolicy, ToolWeight } from '../types.js';
import { searchEnvelopeSchema, searchInputSchema } from './envelope.js';
// Payments are represented as case-voucher additions; there is no separate payment activity type.
export var CaseActivityType;
(function (CaseActivityType) {
    CaseActivityType["Interaction"] = "INTERACTION";
    CaseActivityType["Update"] = "UPDATE";
    CaseActivityType["StatusChange"] = "STATUS_CHANGE";
    CaseActivityType["CaseVoucherAdded"] = "CASE_VOUCHER_ADDED";
    CaseActivityType["DocumentAdded"] = "DOCUMENT_ADDED";
    CaseActivityType["TribunalEvent"] = "TRIBUNAL_EVENT";
})(CaseActivityType || (CaseActivityType = {}));
export var CaseHistoryRefType;
(function (CaseHistoryRefType) {
    CaseHistoryRefType["Interaction"] = "INTERACTION";
    CaseHistoryRefType["Update"] = "UPDATE";
    CaseHistoryRefType["Document"] = "DOCUMENT";
    CaseHistoryRefType["Voucher"] = "VOUCHER";
    CaseHistoryRefType["TribunalCase"] = "TRIBUNAL_CASE";
    CaseHistoryRefType["None"] = "NONE";
})(CaseHistoryRefType || (CaseHistoryRefType = {}));
const filtersSchema = z.object({
    types: z
        .array(z.enum(CaseActivityType))
        .optional()
        .describe('When omitted, all activity types are included.'),
    direction: z
        .enum(Direction)
        .optional()
        .describe('Applies to interactions only; ignored for other activity types.'),
    since: z.iso.datetime().optional(),
});
const itemSchema = z.object({
    activityType: z.enum(CaseActivityType),
    occurredAt: z.iso.datetime(),
    summary: z
        .string()
        .describe("One-line human-readable description of what happened. For UPDATE activities, this includes the update's free-text `update` field when present. Debtor-authored content is wrapped in <debtor_content> tags."),
    refId: z
        .uuid()
        .nullable()
        .describe('Entity id this activity refers to. Pass to the matching get tool based on refType: INTERACTION → get_interaction, UPDATE → get_update, VOUCHER → get_voucher, TRIBUNAL_CASE → get_tribunal_case, DOCUMENT → get_document, NONE → no entity to fetch (status changes etc.).'),
    refType: z.enum(CaseHistoryRefType),
    createdBy: PerformedBySchema.nullable(),
    direction: z.enum(Direction).nullable(),
});
const facetsSchema = z.object({
    byActivityType: z.record(z.enum(CaseActivityType), z.number().int()),
    byDirection: z.record(z.enum(Direction), z.number().int()),
});
const inputSchema = searchInputSchema(filtersSchema);
const outputSchema = searchEnvelopeSchema(itemSchema, facetsSchema);
export const searchCaseHistoryTool = {
    name: ToolName.SearchCaseHistory,
    category: ToolCategory.CaseInfo,
    weight: ToolWeight.Light,
    version: 1,
    defaultPolicy: ToolPolicy.AutoRun,
    llmDescription: 'Read the chronological timeline of activity on this case (interactions, updates, payments, document uploads, tribunal events, prior agent runs, etc.), most recent first. This is your "what has been happening here" view — the same one a caseworker scans on opening a case. Drill into specific items with the matching get_X tool using refId.',
    inputSchema,
    outputSchema,
};
//# sourceMappingURL=search-case-history.js.map